import React, { useState } from 'react';
import api from './api';

const SEV_COLORS = {
  error:   { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', icon: '!',  dot: '#dc2626' },
  warning: { bg: '#fffbeb', border: '#fde68a', text: '#d97706', icon: '!',  dot: '#f59e0b' },
  info:    { bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb', icon: 'i',  dot: '#3b82f6' },
  ok:      { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a', icon: '',   dot: '#22c55e' },
};

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: '24px 20px', fontFamily: "'Inter', system-ui, sans-serif" },
  card: { background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  badge: (color) => ({ display: 'inline-block', background: color, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700, marginLeft: 8 }),
  btnScan: { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginRight: 12 },
  btnClean: { background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnSmall: { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  mono: { fontFamily: 'monospace', fontSize: 12, background: '#f1f5f9', color: '#334155', borderRadius: 6, padding: '2px 8px', fontWeight: 600 },
};

export default function DanglingEntries() {
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});

  const scan = async () => {
    setLoading(true); setMessage(''); setScanData(null);
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/patients/scan-anomalies`);
      setScanData(res.data);
    } catch (e) {
      setMessage('Error: ' + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  // ── Dangling cleanup actions (kept from original) ──
  const cleanAll = async () => {
    if (!window.confirm('This will permanently delete ALL orphan records. Continue?')) return;
    setCleaning(true); setMessage('');
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/patients/dangling-entries/cleanup`, {});
      setMessage(res.data.message);
      scan();
    } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
    setCleaning(false);
  };

  const cleanOne = async (patientCard) => {
    if (!window.confirm(`Delete all orphan records for ${patientCard}?`)) return;
    setCleaning(true); setMessage('');
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/patients/dangling-entries/cleanup`, { patientCard });
      setMessage(res.data.message);
      scan();
    } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
    setCleaning(false);
  };

  const cleanCollection = async (collection) => {
    if (!window.confirm('Delete ALL orphan records from ' + collection + '?')) return;
    setCleaning(true); setMessage('');
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/patients/dangling-entries/cleanup-collection`, { collection });
      setMessage(res.data.message);
      scan();
    } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
    setCleaning(false);
  };

  const toggleSelect = (card) => setSelected(p => ({ ...p, [card]: !p[card] }));
  const selectedCards = Object.keys(selected).filter(k => selected[k]);
  const cleanSelected = async () => {
    if (selectedCards.length === 0) return;
    if (!window.confirm('Delete all orphan records for ' + selectedCards.length + ' selected patient(s)?')) return;
    setCleaning(true); setMessage('');
    try {
      for (let i = 0; i < selectedCards.length; i++) {
        await api.post(`${process.env.REACT_APP_URL}/patients/dangling-entries/cleanup`, { patientCard: selectedCards[i] });
      }
      setMessage('Cleaned ' + selectedCards.length + ' patient(s) orphan records.');
      setSelected({});
      scan();
    } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
    setCleaning(false);
  };

  // ── Helper: find a section by id ──
  const getSection = (id) => scanData?.sections?.find(s => s.id === id);

  // ── Severity badge ──
  const SevBadge = ({ severity, text }) => {
    const c = SEV_COLORS[severity] || SEV_COLORS.info;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot }} />
        {text}
      </span>
    );
  };

  // ── Section header (collapsible) ──
  const SectionHeader = ({ section }) => {
    const isOpen = expanded[section.id];
    return (
      <div
        style={{ ...S.card, cursor: 'pointer', borderLeft: `4px solid ${(SEV_COLORS[section.severity] || SEV_COLORS.info).dot}` }}
        onClick={() => toggle(section.id)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 200 }}>
            {section.title}
          </span>
          <SevBadge severity={section.severity} text={section.summary} />
          <span style={{ fontSize: 16, color: '#94a3b8' }}>{isOpen ? '▲' : '▼'}</span>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{section.description}</div>
      </div>
    );
  };

  // ── Overview stats ──
  const totalIssues = scanData ? scanData.sections.filter(s => s.severity === 'error' || s.severity === 'warning').length : 0;
  const totalErrors = scanData ? scanData.sections.filter(s => s.severity === 'error').length : 0;
  const totalWarnings = scanData ? scanData.sections.filter(s => s.severity === 'warning').length : 0;

  return (
    <div style={S.page}>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Scan Anomalies</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
        Comprehensive data integrity audit — orphans, duplicates, gaps, missing fields, branch variants, and more
      </div>

      <div style={{ marginBottom: 20 }}>
        <button style={S.btnScan} onClick={scan} disabled={loading}>
          {loading ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>

      {loading && (
        <div style={{ fontSize: 14, color: '#64748b', padding: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
          Scanning all collections for anomalies...
        </div>
      )}
      {message && <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600, marginTop: 8, marginBottom: 12 }}>{message}</div>}

      {scanData && (
        <>
          {/* ── Overview Card ── */}
          <div style={S.card}>
            <div style={{ display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Total Patients</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb' }}>{scanData.totalPatients}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Checks Run</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#334155' }}>{scanData.sections.length}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Errors</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: totalErrors > 0 ? '#dc2626' : '#16a34a' }}>{totalErrors}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Warnings</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: totalWarnings > 0 ? '#d97706' : '#16a34a' }}>{totalWarnings}</div>
              </div>
              {totalIssues === 0 && (
                <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>All checks passed</div>
              )}
            </div>
          </div>

          {/* ═══ SECTION: Dangling Entries ═══ */}
          {(() => {
            const sec = getSection('dangling');
            if (!sec) return null;
            const d = sec.data;
            const pi = d.patientInfo || {};
            // Group orphans by patientCard
            const grouped = {};
            (d.collections || []).forEach(c => {
              Object.entries(c.byPatientCard).forEach(([card, count]) => {
                if (!grouped[card]) grouped[card] = { collections: [], totalRecords: 0, name: pi[card]?.name || '', number: pi[card]?.number || '' };
                grouped[card].collections.push({ collection: c.collection, count });
                grouped[card].totalRecords += count;
              });
            });
            const allOrphanCards = Object.keys(grouped);

            return (
              <>
                <SectionHeader section={sec} />
                {expanded.dangling && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    {/* Action bar */}
                    {d.totalOrphans > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '10px 16px', background: '#f1f5f9', borderRadius: 10, flexWrap: 'wrap' }}>
                        <button style={S.btnClean} onClick={cleanAll} disabled={cleaning}>
                          {cleaning ? 'Cleaning...' : 'Clean All Orphans'}
                        </button>
                        <button onClick={selectedCards.length === allOrphanCards.length ? () => setSelected({}) : () => { const sel = {}; allOrphanCards.forEach(c => { sel[c] = true; }); setSelected(sel); }}
                          style={{ border: '1.5px solid #94a3b8', background: '#fff', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#334155' }}>
                          {selectedCards.length === allOrphanCards.length ? 'Deselect All' : 'Select All'}
                        </button>
                        {selectedCards.length > 0 && (
                          <>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{selectedCards.length} selected</span>
                            <button onClick={cleanSelected} disabled={cleaning} style={S.btnSmall}>{cleaning ? 'Deleting...' : 'Delete Selected'}</button>
                          </>
                        )}
                      </div>
                    )}

                    {d.totalOrphans === 0 && (
                      <div style={{ ...S.card, textAlign: 'center', color: '#16a34a', fontWeight: 600, fontSize: 15, padding: 30 }}>
                        All clean — no dangling entries found.
                      </div>
                    )}

                    {/* Collection breakdown */}
                    {(d.collections || []).length > 0 && (
                      <div style={S.card}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Collection-wise Breakdown</div>
                        <div style={{ display: 'grid', gap: 6 }}>
                          {d.collections.sort((a, b) => b.count - a.count).map(c => (
                            <div key={c.collection} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 12px', background: '#fef2f2', borderRadius: 8 }}>
                              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.collection}</span>
                              <span style={S.badge('#dc2626')}>{c.count}</span>
                              <button onClick={() => cleanCollection(c.collection)} disabled={cleaning}
                                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Per-patient orphan cards */}
                    {allOrphanCards.map(card => {
                      const g = grouped[card];
                      const isOpen = expanded['d_' + card];
                      return (
                        <div key={card} style={{ ...S.card, padding: '14px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => toggle('d_' + card)}>
                            <input type="checkbox" checked={!!selected[card]} onClick={e => e.stopPropagation()} onChange={() => toggleSelect(card)}
                              style={{ width: 17, height: 17, accentColor: '#2563eb', cursor: 'pointer' }} />
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #ef4444, #f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
                              {(g.name || card)[0]?.toUpperCase() || '?'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{g.name || 'Unknown'}</span>
                                <span style={S.mono}>{card}</span>
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                {g.number ? `Phone: ${g.number}` : 'Phone: —'} — {g.totalRecords} orphan(s) across {g.collections.length} collection(s)
                              </div>
                            </div>
                            <button style={S.btnSmall} onClick={e => { e.stopPropagation(); cleanOne(card); }} disabled={cleaning}>Clean</button>
                            <span style={{ fontSize: 14, color: '#94a3b8' }}>{isOpen ? '▲' : '▼'}</span>
                          </div>
                          {isOpen && (
                            <div style={{ marginTop: 10, marginLeft: 52, borderLeft: '2px solid #fee2e2', paddingLeft: 14 }}>
                              {g.collections.map(c => (
                                <div key={c.collection} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 150 }}>{c.collection}</span>
                                  <span style={S.badge('#dc2626')}>{c.count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Duplicate Patients ═══ */}
          {(() => {
            const sec = getSection('duplicates');
            if (!sec) return null;
            // Determine which patient to keep (highest data: appointments, sessions, bills, consultation)
            const scorePatient = (p) => (p.completedSessions || 0) * 10 + (p.appointments || 0) * 3 + (p.bills || 0) * 5 + (p.hasConsultation ? 20 : 0) + (p.hasExercises ? 10 : 0) + (p.sequences || 0) * 2;
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.duplicates && sec.data.groups.length > 0 && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 12, fontSize: 12, color: '#92400e' }}>
                      <strong>How to decide:</strong> Keep the patient with more data (sessions, consultation, bills). The "Keep" badge marks the recommended one. Delete the empty/less-used duplicate.
                    </div>
                    {sec.data.groups.map((g, gi) => {
                      const scored = g.patients.map(p => ({ ...p, _score: scorePatient(p) })).sort((a, b) => b._score - a._score);
                      const bestCard = scored[0]?.patientCard;
                      return (
                        <div key={gi} style={{ ...S.card, padding: '16px 20px', marginBottom: 14 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                            Duplicate Group: {g.patients[0]?.name} / {g.patients[0]?.number}
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>({g.patients.length} entries)</span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#334155' }}>Card</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Status</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Sessions</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Appts</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Bills</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Consultation</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Exercises</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Charges</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'left' }}>Last Visit</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'left' }}>Registered</th>
                                  <th style={{ padding: '8px 6px', textAlign: 'center' }}>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {scored.map((p, j) => {
                                  const isKeep = p.patientCard === bestCard;
                                  const hasData = p._score > 0;
                                  return (
                                    <tr key={j} style={{ background: isKeep ? '#f0fdf4' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                                      <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                                        <span style={S.mono}>{p.patientCard}</span>
                                        {isKeep && <span style={{ ...S.badge('#16a34a'), marginLeft: 6, fontSize: 10 }}>KEEP</span>}
                                      </td>
                                      <td style={{ textAlign: 'center' }}><span style={S.badge(p.status === 'enrolled' ? '#16a34a' : '#64748b')}>{p.status || '—'}</span></td>
                                      <td style={{ textAlign: 'center', fontWeight: 700, color: p.completedSessions > 0 ? '#16a34a' : '#94a3b8' }}>{p.completedSessions || 0}</td>
                                      <td style={{ textAlign: 'center' }}>{p.appointments || 0}</td>
                                      <td style={{ textAlign: 'center' }}>{p.bills || 0}</td>
                                      <td style={{ textAlign: 'center' }}>{p.hasConsultation ? <span style={{ color: '#16a34a', fontWeight: 700 }}>Yes</span> : <span style={{ color: '#dc2626' }}>No</span>}</td>
                                      <td style={{ textAlign: 'center' }}>{p.hasExercises ? <span style={{ color: '#16a34a', fontWeight: 700 }}>Yes</span> : <span style={{ color: '#dc2626' }}>No</span>}</td>
                                      <td style={{ textAlign: 'center', fontSize: 11 }}>{p.overallVisitCharges ? '₹' + Number(p.overallVisitCharges).toLocaleString('en-IN') : '—'}</td>
                                      <td style={{ fontSize: 11 }}>{fmtDate(p.lastvisitdate)}</td>
                                      <td style={{ fontSize: 11 }}>{fmtDate(p.registrationDate)}</td>
                                      <td style={{ textAlign: 'center' }}>
                                        {!isKeep && !hasData ? (
                                          <button
                                            style={{ ...S.btnSmall, fontSize: 11 }}
                                            onClick={async () => {
                                              if (!window.confirm('Delete patient ' + p.patientCard + ' and ALL associated data? This cannot be undone.')) return;
                                              try {
                                                await api.delete(process.env.REACT_APP_URL + '/patients/profiles/delete/' + p.patientCard);
                                                setMessage('Deleted ' + p.patientCard);
                                                scan(); // re-scan
                                              } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
                                            }}
                                          >Delete</button>
                                        ) : !isKeep && hasData ? (
                                          <button
                                            style={{ ...S.btnSmall, background: '#f59e0b', fontSize: 11 }}
                                            onClick={async () => {
                                              if (!window.confirm('WARNING: This patient has ' + p.completedSessions + ' sessions, ' + p.bills + ' bills. Delete ' + p.patientCard + ' and ALL data? Consider manually reviewing first.')) return;
                                              try {
                                                await api.delete(process.env.REACT_APP_URL + '/patients/profiles/delete/' + p.patientCard);
                                                setMessage('Deleted ' + p.patientCard);
                                                scan();
                                              } catch (e) { setMessage('Error: ' + (e.response?.data?.error || e.message)); }
                                            }}
                                          >Delete (has data!)</button>
                                        ) : (
                                          <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11 }}>Primary</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Card Gaps ═══ */}
          {(() => {
            const sec = getSection('cardGaps');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.cardGaps && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    {sec.data.prefixes.map((p, i) => (
                      <div key={i} style={{ ...S.card, padding: '14px 20px' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                          Prefix: {p.prefix} — {p.count} patients — Range: {p.prefix}{String(p.min).padStart(6, '0')} to {p.prefix}{String(p.max).padStart(6, '0')}
                        </div>
                        {p.totalGaps > 0 ? (
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            <span style={{ color: '#d97706', fontWeight: 700 }}>{p.totalGaps} gap(s)</span>
                            {': '}
                            {p.gaps.join(', ')}
                            {p.totalGaps > 50 && ` ... and ${p.totalGaps - 50} more`}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>No gaps</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Missing Required Fields ═══ */}
          {(() => {
            const sec = getSection('missingFields');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.missingFields && sec.data.fields.length > 0 && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    {sec.data.fields.map((f, i) => (
                      <div key={i} style={{ ...S.card, padding: '14px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>Missing "{f.field}"</span>
                          <span style={S.badge('#dc2626')}>{f.count}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Affected: {f.samples.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Status Distribution ═══ */}
          {(() => {
            const sec = getSection('statusDist');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.statusDist && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={S.card}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {sec.data.statuses.map((s, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 12px', background: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 6 }}>
                            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#334155' }}>{s.status}</span>
                            <span style={S.badge('#64748b')}>{s.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Branch Variants ═══ */}
          {(() => {
            const sec = getSection('branchVariants');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.branchVariants && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={S.card}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                        Canonical: <span style={S.mono}>{sec.data.canonical}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>All branch values found across collections:</div>
                      <div style={{ display: 'grid', gap: 4 }}>
                        {sec.data.allBranches.map((b, i) => {
                          const isVariant = sec.data.variants.includes(b);
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 10px', background: isVariant ? '#fffbeb' : '#f0fdf4', borderRadius: 6, border: isVariant ? '1px solid #fde68a' : '1px solid #bbf7d0' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: isVariant ? '#92400e' : '#166534' }}>{b}</span>
                              {isVariant && <span style={S.badge('#d97706')}>variant</span>}
                              {b === sec.data.canonical && <span style={S.badge('#16a34a')}>canonical</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Enrolled Without Consultation ═══ */}
          {(() => {
            const sec = getSection('noConsultation');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.noConsultation && sec.data.patients.length > 0 && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={S.card}>
                      {sec.data.total > 50 && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Showing first 50 of {sec.data.total}</div>}
                      <div style={{ display: 'grid', gap: 4 }}>
                        {sec.data.patients.map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '5px 10px', background: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 6 }}>
                            <span style={S.mono}>{p.patientCard}</span>
                            <span style={{ fontSize: 13, color: '#334155', flex: 1 }}>{p.name}</span>
                            <span style={S.badge('#d97706')}>{p.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Orphan Appointment References ═══ */}
          {(() => {
            const sec = getSection('orphanAppointments');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.orphanAppointments && sec.data.cards.length > 0 && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={S.card}>
                      {sec.data.total > 50 && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Showing first 50 of {sec.data.total}</div>}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {sec.data.cards.map((c, i) => (
                          <span key={i} style={S.mono}>{c}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══ SECTION: Recently Added Patients ═══ */}
          {(() => {
            const sec = getSection('recentPatients');
            if (!sec) return null;
            return (
              <>
                <SectionHeader section={sec} />
                {expanded.recentPatients && (
                  <div style={{ marginLeft: 8, marginBottom: 16 }}>
                    <div style={S.card}>
                      {sec.data.patients.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#64748b' }}>No patients added in the last 7 days.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 4 }}>
                          {sec.data.patients.map((p, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 10px', background: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 6, flexWrap: 'wrap' }}>
                              <span style={S.mono}>{p.patientCard}</span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', minWidth: 120 }}>{p.name}</span>
                              <span style={{ fontSize: 12, color: '#64748b' }}>Ph: {p.number}</span>
                              <span style={S.badge(p.status === 'enrolled' ? '#16a34a' : p.status === 'provisional' ? '#3b82f6' : '#64748b')}>{p.status}</span>
                              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                                {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
